import { formatDisplayTitle } from './formatProductTitle';
import { firstProductImage, groupShelfImage, thumbForGroup } from './catalog/catalogMedia';

export { firstProductImage, groupShelfImage, thumbForGroup };

/** Reserved slugs — not shown as hub tiles (`/shop/:cat/all` is the full-category list). */
const RESERVED_HUB_GROUP_KEYS = new Set(['all']);

export function isHubShopGroupKey(key) {
  const g = String(key || '').trim().toLowerCase();
  return Boolean(g) && !RESERVED_HUB_GROUP_KEYS.has(g);
}

/**
 * Build hub tiles (type / subcategory groups) for a category from admin taxonomy + products.
 */
export function buildCategoryGroupCards(meta, productList) {
  const cards = new Map();
  const list = productList || [];

  (meta?.subcategories || []).forEach((s) => {
    const raw = String(s.name || s.slug || '').trim() || s.slug;
    cards.set(s.slug, {
      key: s.slug,
      label: formatDisplayTitle(raw),
      sort: s.sort_order,
      cover_image: String(s.cover_image ?? '').trim(),
    });
  });

  (meta?.types || []).forEach((t) => {
    const ex = cards.get(t.slug);
    const raw = String(t.name || t.slug || '').trim() || t.slug;
    const label = formatDisplayTitle(raw);
    const typeCover = String(t.cover_image ?? '').trim();
    if (ex) {
      cards.set(t.slug, {
        ...ex,
        label,
        sort: Math.min(ex.sort, t.sort_order),
        cover_image: ex.cover_image || typeCover,
      });
    } else {
      cards.set(t.slug, { key: t.slug, label, sort: t.sort_order, cover_image: typeCover });
    }
  });

  list.forEach((p) => {
    const typ = (p.type || '').trim();
    if (typ && !cards.has(typ)) {
      cards.set(typ, {
        key: typ,
        label: formatDisplayTitle(typ.replace(/-/g, ' ')),
        sort: 500 + cards.size,
        cover_image: '',
      });
    }
    const sc = (p.subcategory || '').trim();
    if (sc && !cards.has(sc)) {
      cards.set(sc, {
        key: sc,
        label: formatDisplayTitle(sc.replace(/-/g, ' ')),
        sort: 500 + cards.size,
        cover_image: '',
      });
    }
  });

  return [...cards.values()]
    .filter((c) => isHubShopGroupKey(c.key))
    .sort((a, b) => a.sort - b.sort || a.label.localeCompare(b.label));
}

/** Match hub tiles / list filters: group key is product type OR subcategory (case-insensitive). */
export function productMatchesGroup(product, groupKey) {
  if (!groupKey || String(groupKey).trim().toLowerCase() === 'all') return true;
  const g = String(groupKey).trim().toLowerCase();
  const typ = String(product?.type || '').trim().toLowerCase();
  const sub = String(product?.subcategory || '').trim().toLowerCase();
  return typ === g || sub === g;
}

export function countProductsInGroup(productList, groupKey) {
  const g = String(groupKey || '').trim().toLowerCase();
  if (!g) return 0;
  return (productList || []).filter((p) => productMatchesGroup(p, g)).length;
}
